import React from 'react';
import './ValidationAlert.css';

/**
 * Main Validation Alert Component - Modern, Clean Design
 * Displays validation errors and warnings with optional auto-fix
 */
const ValidationAlert = ({ 
  validationResult, 
  stageName = '', 
  onAutoFix = null,
  showDetails = true 
}) => {
  if (!validationResult || validationResult.isValid) {
    return null;
  }

  const { errors = {}, warnings = [] } = validationResult;
  const errorEntries = Object.entries(errors);
  const hasErrors = errorEntries.length > 0;
  const hasWarnings = warnings.length > 0;

  if (!hasErrors && !hasWarnings) {
    return null;
  }

  // Check if there's an aggregate sum error for auto-fix
  const hasAggregateSumError = warnings.some(w => w.type === 'aggregate_sum') || 
                                errors.hasOwnProperty('rocks_blasted_sum');

  return (
    <div className="validation-alerts-modern">
      {hasErrors && (
        <div className="validation-alert-modern error">
          <div className="alert-icon-wrapper">
            <svg className="alert-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
            </svg>
          </div>
          
          <div className="alert-content">
            <div className="alert-header">
              <h4 className="alert-title">Validation Required</h4>
              {stageName && <span className="alert-stage-badge">{stageName}</span>}
            </div>
            
            {showDetails && (
              <div className="alert-body">
                <p className="alert-description">Please fix the following before continuing:</p>
                <div className="error-list-modern">
                  {errorEntries.map(([field, error]) => (
                    <div key={field} className="error-item-modern">
                      <div className="error-bullet"></div>
                      <div className="error-content">
                        <span className="error-text">{error}</span>
                      </div>
                    </div>
                  ))}
                </div>
                
                {hasAggregateSumError && onAutoFix && (
                  <button 
                    onClick={onAutoFix}
                    className="btn-auto-fix-modern"
                    type="button"
                  >
                    <svg className="fix-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
                    </svg>
                    Auto-Fix Aggregate Sum
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {hasWarnings && !hasErrors && (
        <div className="validation-alert-modern warning">
          <div className="alert-icon-wrapper">
            <svg className="alert-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
          </div>
          
          <div className="alert-content">
            <div className="alert-header">
              <h4 className="alert-title">Attention Required</h4>
              {stageName && <span className="alert-stage-badge warning">{stageName}</span>}
            </div>
            
            {showDetails && (
              <div className="alert-body">
                <div className="warning-list-modern">
                  {warnings.map((warning, idx) => (
                    <div key={idx} className="warning-item-modern">
                      <div className="warning-bullet"></div>
                      <div className="warning-content">
                        <span className="warning-text">{warning.message}</span>
                      </div>
                    </div>
                  ))}
                </div>
                
                {hasAggregateSumError && onAutoFix && (
                  <button 
                    onClick={onAutoFix}
                    className="btn-auto-fix-modern warning"
                    type="button"
                  >
                    <svg className="fix-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
                    </svg>
                    Auto-Fix Aggregate Sum
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Compact validation indicator (for inline use)
 */
export const CompactValidationIndicator = ({ isValid }) => {
  if (isValid) {
    return (
      <span className="validation-indicator-modern valid">
        <svg viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
        </svg>
      </span>
    );
  }
  return (
    <span className="validation-indicator-modern invalid">
      <svg viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"/>
      </svg>
    </span>
  );
};

/**
 * Field-level validation error (for individual inputs)
 */
export const FieldValidationError = ({ error, show = true }) => {
  if (!show || !error) return null;

  return (
    <div className="field-validation-error-modern">
      <svg className="field-error-icon" viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
      </svg>
      <span className="field-error-text">{error}</span>
    </div>
  );
};

export default ValidationAlert;